'use client';

import { useLayoutEffect, useRef, useState } from 'react';

import { FAQ_ITEMS } from './faq-items';

function FaqItem({
  q,
  a,
  open,
  onToggle,
  index,
}: {
  q: string;
  a: string;
  open: boolean;
  onToggle: () => void;
  index: number;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  // Imperatively drive the max-height transition off `open` + measured content
  // height. Uses direct DOM writes (no setState) to avoid redundant re-renders
  // and the react-hooks/set-state-in-effect lint.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const inner = innerRef.current;
    if (!panel || !inner) return;
    panel.style.maxHeight = open ? `${inner.scrollHeight}px` : '0px';
  }, [open]);

  const panelId = `faq-panel-${index}`;
  const buttonId = `faq-button-${index}`;

  return (
    <div className={`faq-item${open ? ' open' : ''}`}>
      <button
        id={buttonId}
        className="faq-q"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span>{q}</span>
        <svg className="chev" viewBox="0 0 22 22" fill="none" aria-hidden>
          <path
            d="M11 5v12M5 11h12"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <div id={panelId} role="region" aria-labelledby={buttonId} className="faq-a" ref={panelRef}>
        <div ref={innerRef} className="faq-a-inner">
          {a}
        </div>
      </div>
    </div>
  );
}

interface FaqAccordionProps {
  items?: ReadonlyArray<{ q: string; a: string }>;
}

export function FaqAccordion({ items = FAQ_ITEMS }: FaqAccordionProps = {}) {
  const [open, setOpen] = useState<number>(0);

  return (
    <div className="faq">
      {items.map((it, i) => (
        <FaqItem
          key={it.q}
          index={i}
          q={it.q}
          a={it.a}
          open={open === i}
          onToggle={() => setOpen(open === i ? -1 : i)}
        />
      ))}
    </div>
  );
}
